if (typeof ProductHighlightsReveal === 'undefined') {
  class ProductHighlightsReveal extends HTMLElement {
    connectedCallback() {
      this._cur    = 0;
      this._track  = this.querySelector('[data-hlr-track]');
      this._dots   = [];
      this._cards  = [...this.querySelectorAll('[data-hlr-card]')];
      this._prev   = this.querySelector('[data-hlr-prev]');
      this._next   = this.querySelector('[data-hlr-next]');
      this._drag   = { on: false, active: false, startX: 0, startY: 0, lastX: 0, velocity: 0, dir: null, base: 0 };
      this._didDrag = false;
      this._bindEvents();
      this._updateTrack();
      this._updateArrows();
      this._renderDots();
    }

    _bindEvents() {
      // Dots are rendered dynamically — see _renderDots()

      // Arrows
      if (this._prev) this._prev.addEventListener('click', () => this._goTo(this._cur - 1));
      if (this._next) this._next.addEventListener('click', () => this._goTo(this._cur + 1));

      // Drag on slider container — pointer events for mouse, trackpad, and touch
      // Card tap detection is handled in _end via elementFromPoint (setPointerCapture eats click events)
      const slider = this._track ? this._track.parentElement : null;
      if (slider) {
        slider.addEventListener('pointerdown', e => {
          if (e.target.closest('.hlr-arrow')) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.pointerType === 'mouse') {
            try { slider.setPointerCapture(e.pointerId); } catch(_) {}
            slider.style.cursor = 'grabbing';
          }
          this._start(e.clientX, e.clientY);
        });
        slider.addEventListener('pointermove', e => {
          if (!this._drag.on) return;
          this._move(e.clientX, e.clientY, e);
        }, { passive: false });
        slider.addEventListener('pointerup', e => {
          slider.style.cursor = '';
          this._end(e.clientX, e.clientY);
        });
        slider.addEventListener('pointercancel', () => { slider.style.cursor = ''; this._cancel(); });
        slider.addEventListener('dragstart', e => e.preventDefault());
        slider.addEventListener('wheel', e => this._onWheel(e), { passive: true });
      }
      window.addEventListener('resize', () => { this._cancel(); this._updateTrack(); this._updateArrows(); this._renderDots(); });
    }

    // ── Expand / collapse ──────────────────────────────────
    _toggleCard(idx) {
      const card = this._cards[idx];
      if (!card) return;
      const btn      = card.querySelector('[data-hlr-toggle]');
      const expanding = !card.classList.contains('is-expanded');

      // Auto-close any other open card before opening a new one
      if (expanding) {
        this._cards.forEach((c, i) => {
          if (i !== idx && c.classList.contains('is-expanded')) {
            c.classList.remove('is-expanded');
            const b = c.querySelector('[data-hlr-toggle]');
            if (b) b.setAttribute('aria-expanded', 'false');
          }
        });
        card.classList.add('is-expanded');
        if (btn) btn.setAttribute('aria-expanded', 'true');
      } else {
        card.classList.remove('is-expanded');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }
    }

    // ── Slider ─────────────────────────────────────────────
    _getVisibleCols() {
      if (!this._track) return 1;
      const pw = this._track.parentElement.offsetWidth;
      const sw = this._track.children[0]?.offsetWidth || pw;
      return Math.max(1, Math.round(pw / sw));
    }

    _getMax() {
      const n = this._cards.length, vis = this._getVisibleCols();
      // Fewer cards than columns: let each card slide to the start position
      return n < vis ? Math.max(0, n - 1) : Math.max(0, n - vis);
    }

    _goTo(idx) {
      const max = this._getMax();
      this._cur = Math.max(0, Math.min(idx, max));
      this._dots.forEach((d, i) => d.classList.toggle('is-active', i === this._cur));
      this._updateTrack();
      this._updateArrows();
    }

    _renderDots() {
      const dotsEl = this.querySelector('.hlr-dots');
      if (!dotsEl) return;
      const max = this._getMax();

      if (max === 0) {
        dotsEl.style.display = 'none';
        this._dots = [];
        return;
      }

      dotsEl.style.display = '';
      const count = max + 1;

      // Only rebuild DOM if the count changed
      if (dotsEl.children.length !== count) {
        dotsEl.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const btn = document.createElement('button');
          btn.className = 'hlr-dot' + (i === this._cur ? ' is-active' : '');
          btn.dataset.hlrDot = i;
          btn.type = 'button';
          btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          btn.addEventListener('click', () => this._goTo(+btn.dataset.hlrDot));
          dotsEl.appendChild(btn);
        }
        this._dots = [...dotsEl.querySelectorAll('[data-hlr-dot]')];
      }

      // Sync active state in case cur changed without a rebuild
      this._dots.forEach((d, i) => d.classList.toggle('is-active', i === this._cur));
    }

    _updateTrack() {
      if (!this._track) return;
      const pw  = this._track.parentElement.offsetWidth;
      const sw  = this._track.children[0]?.offsetWidth || pw;
      const ctr = sw > pw * 0.75 ? (pw - sw) / 2 : 0;
      this._track.style.transform = `translateX(${ctr - this._cur * sw}px)`;
    }

    _updateArrows() {
      if (this._prev) this._prev.disabled = this._cur === 0;
      if (this._next) this._next.disabled = this._cur >= this._getMax();
    }

    // ── Drag ──────────────────────────────────────────────
    _start(x, y) {
      if (!this._track) return;
      const d = this._drag, pw = this._track.parentElement.offsetWidth,
            sw = this._track.children[0]?.offsetWidth || pw;
      const ctr = sw > pw * 0.75 ? (pw - sw) / 2 : 0;
      d.on = true; d.active = false; d.startX = x; d.startY = y || 0;
      d.lastX = x; d.velocity = 0; d.dir = null;
      d.base = ctr - this._cur * sw;
    }

    _move(x, y, e) {
      const d = this._drag;
      if (!d.on) return;
      const dx = x - d.startX, dy = y !== null ? y - d.startY : 0;
      if (!d.dir) {
        if (Math.sqrt(dx * dx + dy * dy) < 4) return;
        d.dir = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }
      if (d.dir !== 'h') return;
      if (!d.active) { d.active = true; this._track.style.transition = 'none'; }
      if (e) e.preventDefault();
      d.velocity = x - d.lastX; d.lastX = x;
      this._track.style.transform = `translateX(${d.base + dx}px)`;
    }

    _end(x, y) {
      const d = this._drag;
      if (!d.on) return;
      d.on = false;
      if (!d.active) {
        this._didDrag = false;
        // Tap detected — find card under pointer and toggle it
        const el = (y != null) ? document.elementFromPoint(x, y) : null;
        const card = el && el.closest('[data-hlr-card]');
        if (card && card.querySelector('[data-hlr-body]')) {
          const idx = this._cards.indexOf(card);
          if (idx >= 0) this._toggleCard(idx);
        }
        return;
      }
      this._didDrag = true;
      setTimeout(() => { this._didDrag = false; }, 100);
      const dx = d.startX - x, n = this._cards.length;
      let next = this._cur;
      if (Math.abs(dx) > 30 || Math.abs(d.velocity) > 0.2)
        next = dx > 0 ? Math.min(next + 1, n - 1) : Math.max(next - 1, 0);
      this._track.style.transition = '';
      this._goTo(next);
    }

    _cancel() {
      const d = this._drag;
      if (!d.on) return;
      d.on = false; d.active = false;
      if (this._track) { this._track.style.transition = ''; this._updateTrack(); }
    }

    _onWheel(e) {
      const ax = Math.abs(e.deltaX), ay = Math.abs(e.deltaY);
      if (ax < 3 || ay > ax * 2.5) return;
      const now = Date.now();
      if (this._lastWheel && now - this._lastWheel < 200) return;
      this._lastWheel = now;
      this._goTo(e.deltaX > 0 ? this._cur + 1 : this._cur - 1);
    }
  }

  customElements.define('product-highlights-reveal', ProductHighlightsReveal);
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('product-highlights-reveal');
    if (el) el.connectedCallback();
  });
}
